import React, { useContext } from "react"
import { DeepReadonly } from "ts-essentials"
import { KPIContext } from "connected-components/kpi/context/kpi"
import AnalyticsContext, { useAnalyticsWithProperties } from "connected-components/analytics/context"
import { useExtractKPIContextProperties } from "./extract-context-properties"

interface KPIAnalyticsProviderProps {
  children: React.ReactNode
}

/**
 * Provides KPI analytics context with automatic property extraction
 * and "KPI" prefix for all events.
 *
 * This provider wraps children with analytics context that includes:
 * - KPI-specific context properties (selectedTab, selectedGroup)
 * - "KPI" prefix for all tracked events
 * - Inheritance from parent analytics contexts if nested (Dashboard properties)
 *
 * Must be used within a KPIContext.Provider.
 */
export function KPIAnalyticsProvider({
  children,
}: DeepReadonly<KPIAnalyticsProviderProps>) {
  const kpiContext = useContext(KPIContext)

  const contextProperties = useExtractKPIContextProperties(kpiContext)

  const analyticsContextValue = useAnalyticsWithProperties(contextProperties, "KPI")

  return (
    <AnalyticsContext.Provider value={analyticsContextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export default KPIAnalyticsProvider
